import { useEffect, useState } from "react";

const StripePricingTable = ({ pricingTableId, clientSecretKey, publishableKey }) => {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const scriptId = "stripe-pricing-table-js";
    const maxRetries = 3;

    // Check if custom element is already defined
    if (customElements.get('stripe-pricing-table')) {
      setIsScriptLoaded(true);
      return;
    }

    // Check if script already exists
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      // Wait for custom element to be defined
      customElements.whenDefined('stripe-pricing-table')
        .then(() => {
          console.log('Stripe pricing table custom element is ready');
          setIsScriptLoaded(true);
          setHasError(false);
        })
        .catch(err => {
          console.error('Error waiting for custom element:', err);
          if (retryCount < maxRetries) {
            setRetryCount(prev => prev + 1);
            existingScript.remove();
          } else {
            setHasError(true);
          }
        });
      return;
    }

    // Load the script
    const loadScript = () => {
      const script = document.createElement("script");
      script.src = "https://js.stripe.com/v3/pricing-table.js";
      script.async = true;
      script.id = scriptId;
      script.crossOrigin = "anonymous";
      
      script.onload = () => {
        console.log('Stripe script loaded successfully');
        // Wait for custom element to be registered
        customElements.whenDefined('stripe-pricing-table')
          .then(() => {
            console.log('Stripe pricing table custom element is ready');
            setIsScriptLoaded(true);
            setHasError(false);
          })
          .catch(err => {
            console.error('Error waiting for custom element:', err);
            if (retryCount < maxRetries) {
              setRetryCount(prev => prev + 1);
              script.remove();
            } else {
              setHasError(true);
            }
          });
      };

      script.onerror = (error) => {
        console.error('Failed to load Stripe script:', error);
        if (retryCount < maxRetries) {
          console.log(`Retrying... (${retryCount + 1}/${maxRetries})`);
          setRetryCount(prev => prev + 1);
          script.remove();
        } else {
          setHasError(true);
        }
      };

      document.head.appendChild(script);
    };

    loadScript();

    // Cleanup function
    return () => {
      // Don't remove the script on unmount to prevent re-loading
    };
  }, [retryCount]);

  // Don't render anything until we have all required props
  if (!pricingTableId || !publishableKey || !clientSecretKey) {
    console.log('Missing required props:', { pricingTableId, publishableKey, clientSecretKey });
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center', 
        color: '#666',
        fontSize: '14px'
      }}>
        Missing subscription configuration. Please contact support.
      </div>
    );
  }

  // Show error state
  if (hasError) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center', 
        color: '#d32f2f',
        border: '1px solid #d32f2f',
        borderRadius: '8px',
        margin: '20px',
        backgroundColor: '#ffebee'
      }}>
        <h3>Unable to load subscription plans</h3>
        <p>Please try refreshing the page. If the problem persists, contact support.</p>
        <button 
          onClick={() => window.location.reload()} 
          style={{
            padding: '10px 20px',
            marginTop: '10px',
            backgroundColor: '#d32f2f',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Refresh Page
        </button>
      </div>
    );
  }

  // Show loading state while script is loading
  if (!isScriptLoaded) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center',
        minHeight: '400px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          border: '3px solid #f3f3f3',
          borderTop: '3px solid #3498db',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ marginTop: '20px', color: '#666' }}>Loading subscription plans...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  console.log('Rendering Stripe pricing table with props:', {
    pricingTableId,
    publishableKey: publishableKey.substring(0, 10) + '...',
    clientSecretKey: clientSecretKey.substring(0, 10) + '...'
  });

  // Only render the custom element once script is loaded and element is defined
  return (
    <div style={{ 
      paddingTop: '20px', 
      paddingBottom: '20px', 
      width: '100%', 
      marginTop: '20px' 
    }}>
      <stripe-pricing-table
        pricing-table-id={pricingTableId}
        publishable-key={publishableKey}
        customer-session-client-secret={clientSecretKey}
      ></stripe-pricing-table>
    </div>
  );
};

export default StripePricingTable;